import { formatINR } from '../../../shared/utils/formatters';
import React, { useState, useEffect } from 'react'
import { Table, Tag, Button, Card, Space, Typography, message, Select, Modal } from 'antd'
import { PlusOutlined, PlayCircleOutlined, CheckCircleOutlined, InboxOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../../shared/api/axios-instance'

const { Title } = Typography

interface Campaign {
    id: number
    campaign_code: string
    campaign_name: string
    reward_type: string
    start_date: string
    end_date: string
    status: string
    priority: number
    milestones: Milestone[]
}

interface Milestone {
    id: number
    sequence: number
    milestone_identifier: string
    reward_value: number
}

const STATUS_COLORS: Record<string, string> = {
    draft: 'default',
    active: 'success',
    completed: 'blue',
    archived: 'default',
}

const STATUS_TRANSITIONS: Record<string, { label: string; icon: React.ReactNode; next: string }> = {
    draft: { label: 'Activate', icon: <PlayCircleOutlined />, next: 'active' },
    active: { label: 'Complete', icon: <CheckCircleOutlined />, next: 'completed' },
    completed: { label: 'Archive', icon: <InboxOutlined />, next: 'archived' },
}

export const CampaignListScreen: React.FC = () => {
    const navigate = useNavigate()
    const [campaigns, setCampaigns] = useState<Campaign[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)

    const fetchCampaigns = async () => {
        try {
            setIsLoading(true)
            const params: any = {}
            if (statusFilter) params.status = statusFilter

            const response = await apiClient.get('/admin/campaigns', { params })
            setCampaigns(response.data.data || [])
        } catch (error: any) {
            message.error(error.message || 'Failed to fetch campaigns')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchCampaigns()
    }, [statusFilter])

    const handleTransition = async (campaignId: number, newStatus: string, campaignName: string) => {
        Modal.confirm({
            title: `Transition Campaign`,
            content: `Are you sure you want to change "${campaignName}" to "${newStatus.toUpperCase()}"? ${newStatus === 'completed' ? 'This action is IRREVERSIBLE.' : ''}`,
            onOk: async () => {
                try {
                    await apiClient.put(`/admin/campaigns/${campaignId}/transition`, { status: newStatus })
                    message.success(`Campaign transitioned to ${newStatus}`)
                    fetchCampaigns()
                } catch (error: any) {
                    message.error(error.response?.data?.message || 'Failed to transition campaign')
                }
            },
        })
    }

    const columns = [
        {
            title: 'Code',
            dataIndex: 'campaign_code',
            key: 'code',
            render: (code: string) => <Tag color="purple">{code}</Tag>,
        },
        {
            title: 'Name',
            dataIndex: 'campaign_name',
            key: 'name',
        },
        {
            title: 'Date Range',
            key: 'dates',
            render: (_: any, record: Campaign) => (
                <span>
                    {new Date(record.start_date).toLocaleDateString()} — {new Date(record.end_date).toLocaleDateString()}
                </span>
            ),
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            sorter: (a: Campaign, b: Campaign) => a.priority - b.priority,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={STATUS_COLORS[status] || 'default'}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Milestones',
            key: 'milestones',
            render: (_: any, record: Campaign) => (
                <Space direction="vertical" size="small">
                    {record.milestones?.map((m) => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {m.milestone_identifier}: {formatINR(m.reward_value.toFixed(2))}
                        </div>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Campaign) => {
                const transition = STATUS_TRANSITIONS[record.status]
                if (!transition) return null
                return (
                    <Button
                        size="small"
                        type="primary"
                        icon={transition.icon}
                        onClick={() => handleTransition(record.id, transition.next, record.campaign_name)}
                        danger={record.status === 'active'} // Make "Complete" visually distinct
                    >
                        {transition.label}
                    </Button>
                )
            },
        },
    ]

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>Reward Campaigns</Title>
                <Space>
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        value={statusFilter}
                        onChange={setStatusFilter}
                        style={{ width: 160 }}
                        options={[
                            { label: 'Draft', value: 'draft' },
                            { label: 'Active', value: 'active' },
                            { label: 'Completed', value: 'completed' },
                            { label: 'Archived', value: 'archived' },
                        ]}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/campaigns/create')}
                    >
                        Create Campaign
                    </Button>
                </Space>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={campaigns}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    )
}
